import type { AlertErrorHandlerFactory } from '@/features/shell'
import { type InsightStore } from '@/features/jobs/details/insights/InsightStore'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import type {
  ConversationFn,
  FetchHumanMessagesTotalCountFn,
} from '@/features/messaging'
import { computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { type ObservedValueOf, of } from 'rxjs'
import { filter, switchMap } from 'rxjs/operators'
import { withAbortSignal } from '@/utils/rx-util'
import { tapAction, toStream } from '@/utils/mobx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { Revalidation } from '@/infra/revalidation'
import { dateFormatters } from '@/utils/date-util'

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
})

function formatDateTime(date: Date) {
  return `${dateFormatter.format(date)} at ${dateFormatters.timeDefault.format(date)}`
}

/**
 * View model for the card showing AI-generated insights about a job.
 */
export class JobInsightsCardViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _revalidation = new Revalidation(() => this.fetch.pending)

  @observable.ref private accessor _conversation: ObservedValueOf<
    ReturnType<ConversationFn>
  > = undefined

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    private readonly _jobInsightStore: InsightStore,
    private readonly _conversationFn: ConversationFn,
    private readonly _fetchHumanMessagesTotalCount: FetchHumanMessagesTotalCountFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this._lifecycle.manage(
      toStream(() => this._job.conversation?.id, true).pipe(
        switchMap((id) => (id ? this._conversationFn(id) : of(undefined))),
        tapAction((conversation) => {
          this._conversation = conversation
        }),
      ),
    )
    this._lifecycle.manage(
      // Fresh insights go stale as the job changes.
      this._jobStore.jobUpdatedEvents$.pipe(
        filter((e) => e.job.id === this._job.id),
        filter(() => this.isLatestInsightsAvailable && !this.isAnyIrrelevant),
        switchMap(() =>
          withAbortSignal((signal) =>
            this._jobInsightStore
              .fetchLatestJobInsights(this._job.id, signal)
              // A failed refresh keeps the insights already shown, and the
              // next job update tries again.
              .catch(() => undefined),
          ),
        ),
      ),
    )
  }

  /**
   * Whether the job's conversation has any human messages to generate
   * insights from.
   */
  @computed
  get isMoreThanZeroMessagesAvailable() {
    const conversation = this._conversation
    if (conversation === undefined) {
      return false
    }

    return conversation.humanMessagesCount > 0
  }

  /**
   * The most recently generated insights for the job.
   */
  @computed
  get latestInsights() {
    return this._jobInsightStore.latestInsightsForJob(this._job.id)
  }

  /**
   * Whether any insights have been generated.
   */
  @computed
  get isLatestInsightsAvailable() {
    return this.latestInsights.length > 0
  }

  /**
   * When the latest insights were generated, if any were.
   */
  @computed
  get latestInsightGeneratedDate() {
    return this.latestInsights[0]?.dateGenerated ?? null
  }

  /**
   * Whether any of the latest insights went stale.
   */
  @computed
  get isAnyIrrelevant() {
    return this.latestInsights.some((i) => i.isIrrelevant)
  }

  /**
   * Whether the next generation replaces stale insights rather than adding
   * more.
   */
  @computed
  get isRegenerateNext() {
    return this.isLatestInsightsAvailable && this.isAnyIrrelevant
  }

  /**
   * The formatted time the latest insights were generated.
   */
  @computed
  get lastGeneratedTimeString() {
    if (this.latestInsightGeneratedDate === null) {
      return null
    }

    const date = new Date(this.latestInsightGeneratedDate)
    return formatDateTime(date)
  }

  /**
   * The label of the generate button, or `null` when it is hidden.
   */
  @computed
  get insightsLabelText() {
    if (this.regenerateOrGenerateMore.pending && this.isRegenerateNext) {
      return 'Regenerating'
    }

    if (this.regenerateOrGenerateMore.pending) {
      return 'Generating'
    }

    if (this.isRegenerateNext) {
      return 'Regenerate'
    }

    if (!this.isAnyIrrelevant && this.latestInsights.length < 5) {
      return 'Generate More'
    }

    return null
  }

  /**
   * Regenerates stale insights, or generates more.
   */
  regenerateOrGenerateMore = task.resolved(async () => {
    await this._jobInsightStore
      .generateOrRefreshJobInsights(this._job.id)
      .catch(this._createAlertErrorHandler('Generating insights failed'))
  })

  /**
   * Whether the insights are refetching while the previous ones stay on
   * screen.
   */
  @computed
  get isRevalidating() {
    return this._revalidation.isRevalidating
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The card renders a failed load.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.fetch.reset()
  }

  /**
   * Fetches the message count and the latest insights.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._fetchHumanMessagesTotalCount(
      this._job.conversation?.id,
      this._job.workspace.id,
    )
    signal.throwIfAborted()
    await this._jobInsightStore.fetchLatestJobInsights(this._job.id, signal)

    if (this.isRegenerateNext) {
      await this._regenerateIrrelevantInsights(signal)
    }
    this._revalidation.markLoaded()
  })

  /**
   * Regenerates stale insights in the background. The user didn't ask for
   * it, so a failure stays quiet; clicking Regenerate surfaces it.
   */
  private async _regenerateIrrelevantInsights(signal: AbortSignal) {
    await this._jobInsightStore
      .generateOrRefreshJobInsights(this._job.id, signal)
      .catch(() => undefined)
  }
}
